import {
  Close,
  DirectionsCar,
  EmojiEvents,
  FileDownload,
  Gavel,
  HowToReg,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LocalOffer,
  Login,
  OpenInNew,
  People,
  Public,
  RequestQuote,
  Storefront,
  Timer,
  TrendingDown,
  TrendingUp,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
} from "chart.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import styled, { keyframes } from "styled-components";
import { useToastStore, useUserStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { UserRole } from "../../../utils";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import { exportAnunciosExcel, fetchAnunciosNaoVendidos, fetchAnunciosResumo, fetchAnunciosVendidos, fetchDashBoardSales } from "../repository/AdminRepository";
import { DashboardGeral, RelatorioFilterValues } from "../types/types";
import { RelatorioFilterBar } from "./RelatorioFilterBar";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  ChartTooltip,
  Legend,
);

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

const MONTH_NAMES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

function formatCurrency(val: number): string {
  return val.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
  });
}

function formatNumber(val: number): string {
  return val.toLocaleString("pt-BR");
}

function formatMinutesToReadable(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return `${h}h ${m > 0 ? m + "min" : ""}`.trim();
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return `${d}d ${rh > 0 ? rh + "h" : ""}`.trim();
}

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
  onClick?: () => void;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay, onClick }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          cursor: onClick ? "pointer" : "default",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2rem"
            fontWeight={900}
            letterSpacing={-1.5}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.7rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", overflow: "hidden", position: "relative" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: 3, bgcolor: "#e2e8f0" }} />
      </Paper>
    </AnimatedBox>
  );
}

// --- KPI Detail Dialog ---

type KpiType =
  | "publicados"
  | "vendidos"
  | "nao_vendidos"
  | "veiculos_publicados"
  | "veiculos_vendidos"
  | "veiculos_nao_vendidos"
  | "views"
  | "tempo_medio"
  | "compre_agora"
  | "leilao"
  | "cotacoes"
  | "combos"
  | "media_propostas";

interface KpiDetailAnuncioVeiculo {
  id_veiculo: number;
  placa: string;
  veiculo: string;
}

interface KpiDetailAnuncio {
  id_anuncio: number;
  concessionaria: string;
  revenda: string;
  dat_cadastro: string;
  dat_venda: string | null;
  val_venda: number;
  qtd_total_click: number;
  time_sell_minutes: number;
  status: string;
  tipo_anuncio: string;
  veiculos: KpiDetailAnuncioVeiculo[];
}

const KPI_LABELS: Record<KpiType, string> = {
  publicados: "Anúncios Publicados",
  vendidos: "Anúncios Vendidos",
  nao_vendidos: "Anúncios Não Vendidos",
  veiculos_publicados: "Veículos Publicados",
  veiculos_vendidos: "Veículos Vendidos",
  veiculos_nao_vendidos: "Veículos Não Vendidos",
  views: "Views Totais",
  tempo_medio: "Tempo Médio de Venda",
  compre_agora: "Anúncios Compre Agora",
  leilao: "Anúncios Leilão",
  cotacoes: "Anúncios Cotações",
  combos: "Anúncios Combos",
  media_propostas: "Média Propostas",
};

const STATUS_CHIP: Record<string, { color: "success" | "warning" | "error" | "info"; label: string }> = {
  Publicado: { color: "info", label: "Publicado" },
  Vendido: { color: "success", label: "Vendido" },
  Expirado: { color: "warning", label: "Expirado" },
};

const TIPO_CHIP_COLOR: Record<string, string> = {
  "Compre Agora": "#8b5cf6",
  "Leilão": "#f59e0b",
  "Cotação": "#0284c7",
  "Combo": "#14b8a6",
};

function AnuncioRow({ anuncio }: { anuncio: KpiDetailAnuncio }) {
  const [expanded, setExpanded] = useState(false);
  const chip = STATUS_CHIP[anuncio.status] ?? { color: "info" as const, label: anuncio.status };
  const hasMultiple = anuncio.veiculos.length > 1;
  const tipoColor = TIPO_CHIP_COLOR[anuncio.tipo_anuncio] ?? "#64748b";

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded((p) => !p)}
        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fafbfc" }, "& > td": { borderBottom: expanded ? "none" : undefined } }}
      >
        <TableCell sx={{ width: 40, pl: 1, pr: 0 }}>
          <IconButton size="small" sx={{ transition: "transform 0.2s" }}>
            {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569" }}>
          <Tooltip title="Abrir anúncio" arrow>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ cursor: "pointer", color: colorPrimary }}
              onClick={(e) => { e.stopPropagation(); /* TODO: navegar para o anúncio */ }}
            >
              {anuncio.id_anuncio}
              <OpenInNew sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569" }}>
          <Chip
            label={anuncio.tipo_anuncio}
            size="small"
            sx={{ fontWeight: 700, fontSize: "0.68rem", bgcolor: tipoColor + "18", color: tipoColor, border: `1px solid ${tipoColor}30` }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {anuncio.concessionaria}
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {anuncio.revenda}
        </TableCell>
        <TableCell>
          <Chip label={chip.label} color={chip.color} size="small" sx={{ fontWeight: 700, fontSize: "0.7rem" }} />
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569", whiteSpace: "nowrap" }}>{anuncio.dat_cadastro}</TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569", whiteSpace: "nowrap" }}>{anuncio.dat_venda ?? "—"}</TableCell>
        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#10b981", whiteSpace: "nowrap" }}>
          {anuncio.val_venda > 0 ? formatCurrency(anuncio.val_venda) : "—"}
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#0284c7", textAlign: "center" }}>
          {anuncio.qtd_total_click}
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", color: "#475569", textAlign: "center", whiteSpace: "nowrap" }}>
          {anuncio.time_sell_minutes > 0 ? formatMinutesToReadable(anuncio.time_sell_minutes) : "—"}
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          {hasMultiple ? (
            <Chip label={`${anuncio.veiculos.length} veíc.`} size="small" sx={{ fontWeight: 700, fontSize: "0.7rem", bgcolor: "#f5f3ff", color: "#8b5cf6" }} />
          ) : (
            <Typography fontSize="0.78rem" color="#64748b">1 veíc.</Typography>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={12} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1.5, p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography fontSize="0.72rem" fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing={1.2} mb={1}>
                Veículos do anúncio
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, py: 0.8, borderBottom: "1px solid #e2e8f0" }}>
                      Placa
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, py: 0.8, borderBottom: "1px solid #e2e8f0" }}>
                      Veículo
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {anuncio.veiculos.map((v, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1e293b", fontFamily: "monospace", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                        {v.placa}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                        {v.veiculo}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const DETAIL_TABLE_HEADERS = [
  { label: "", width: 40 },
  { label: "ID" },
  { label: "Tipo" },
  { label: "Concessionária" },
  { label: "Revenda" },
  { label: "Status" },
  { label: "Publicação" },
  { label: "Venda" },
  { label: "Valor" },
  { label: "Views" },
  { label: "Tempo" },
  { label: "Veículos" },
];

const KPI_TO_EXPORT_TIPO: Partial<Record<KpiType, string>> = {
  publicados: "publicados",
  veiculos_publicados: "publicados",
  vendidos: "vendidos",
  veiculos_vendidos: "vendidos",
  nao_vendidos: "nao_vendidos",
  veiculos_nao_vendidos: "nao_vendidos",
};

function KpiDetailDialog({
  open,
  kpiType,
  filters,
  onClose,
}: {
  open: boolean;
  kpiType: KpiType | null;
  filters: RelatorioFilterValues;
  onClose: () => void;
}) {
  const [data, setData] = useState<KpiDetailAnuncio[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { setToast } = useToastStore();

  useEffect(() => {
    if (!open || !kpiType) return;
    let cancelled = false;
    setDialogLoading(true);
    setPage(0);

    const idGrupo = filters.grupo !== "todos" ? filters.grupo : null;
    const idConcessionaria = filters.concessionaria !== "todos" ? filters.concessionaria : null;

    const fetchMap: Record<string, () => Promise<any>> = {
      publicados: () => fetchAnunciosResumo(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
      veiculos_publicados: () => fetchAnunciosResumo(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
      vendidos: () => fetchAnunciosVendidos(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
      veiculos_vendidos: () => fetchAnunciosVendidos(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
      nao_vendidos: () => fetchAnunciosNaoVendidos(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
      veiculos_nao_vendidos: () => fetchAnunciosNaoVendidos(filters.dataInicio, filters.dataFim, idGrupo, idConcessionaria),
    };

    const fetcher = fetchMap[kpiType];
    if (!fetcher) {
      setDialogLoading(false);
      return;
    }

    fetcher()
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setData([]);
          setDialogLoading(false);
          return;
        }



        const mapped: KpiDetailAnuncio[] = (res.data as any[])
          .filter((item) => {
            return true;
          })
          .map((item) => ({
            id_anuncio: item.id,
            concessionaria: item.concessionaria ?? "",
            revenda: item.revenda ?? "—",
            dat_cadastro: item.data_publicacao ? new Date(item.data_publicacao).toLocaleDateString("pt-BR") : "",
            dat_venda: item.data_venda ? new Date(item.data_venda).toLocaleDateString("pt-BR") : null,
            val_venda: item.val_venda ?? 0,
            qtd_total_click: item.views ?? 0,
            time_sell_minutes: item.tempo_minuto_pra_venda ?? 0,
            status: item.status ?? "",
            tipo_anuncio: item.tipo_anuncio ?? "",
            veiculos: item.veiculos ?? [],
          }));

        setData(mapped);
        setDialogLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, kpiType, filters]);

  const exportTipo = kpiType ? KPI_TO_EXPORT_TIPO[kpiType] : undefined;

  const handleExportExcel = useCallback(async () => {
    if (!exportTipo) return;
    setExporting(true);
    try {
      const idGrupo = filters.grupo !== "todos" ? filters.grupo : null;
      const idConcessionaria = filters.concessionaria !== "todos" ? filters.concessionaria : null;
      const result = await exportAnunciosExcel(
        filters.dataInicio,
        filters.dataFim,
        exportTipo,
        idGrupo,
        idConcessionaria,
      );
      if (!result.success) {
        setToast({ variant: "error", message: result.message ?? "Erro ao exportar Excel." });
      }
    } finally {
      setExporting(false);
    }
  }, [exportTipo, filters, setToast]);

  const title = kpiType ? KPI_LABELS[kpiType] : "";
  const totalVeiculos = new Set(data.flatMap((a) => a.veiculos.map((v) => v.id_veiculo))).size;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: "85vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Box>
          <Typography fontWeight={800} fontSize="1.1rem" color="#1e293b">
            {title}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mt={0.5}>
            <Typography fontSize="0.75rem" color="#94a3b8" fontWeight={500}>
              {filters.dataInicio} — {filters.dataFim}
              {filters.grupo !== "todos" && ` · Grupo: ${filters.grupo}`}
              {filters.concessionaria !== "todos" && ` · Conc: ${filters.concessionaria}`}
            </Typography>
            {!dialogLoading && (
              <Box display="flex" gap={1.5}>
                <Chip label={`${data.length} anúncios`} size="small" sx={{ fontWeight: 700, fontSize: "0.7rem", bgcolor: colorPrimary + "15", color: colorPrimary }} />
                <Chip label={`${totalVeiculos} veículos`} size="small" sx={{ fontWeight: 700, fontSize: "0.7rem", bgcolor: "#8b5cf615", color: "#8b5cf6" }} />
              </Box>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {exportTipo && !dialogLoading && (
            <Button
              variant="contained"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
              disabled={exporting || data.length === 0}
              onClick={handleExportExcel}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                borderRadius: 2,
                bgcolor: "#10b981",
                "&:hover": { bgcolor: "#059669" },
                px: 2,
              }}
            >
              {exporting ? "Exportando..." : "Exportar Excel"}
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {dialogLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={10}>
            <CircularProgress size={40} sx={{ color: colorPrimary }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {DETAIL_TABLE_HEADERS.map((h) => (
                      <TableCell
                        key={h.label || "expand"}
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          color: "#94a3b8",
                          bgcolor: "#f8fafc",
                          borderBottom: "2px solid #f1f5f9",
                          whiteSpace: "nowrap",
                          py: 1.5,
                          ...(h.width ? { width: h.width } : {}),
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((anuncio) => (
                      <AnuncioRow key={anuncio.id_anuncio} anuncio={anuncio} />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DoughnutLegendItem {
  label: string;
  value: number;
  color: string;
}

function DoughnutLegend({ items }: { items: DoughnutLegendItem[] }) {
  const total = items.reduce((s, i) => s + i.value, 0);
  return (
    <Box display="flex" justifyContent="center" gap={4} mt={2}>
      {items.map((item) => {
        const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
        return (
          <Box key={item.label} display="flex" alignItems="center" gap={1}>
            <Box width={10} height={10} borderRadius="50%" bgcolor={item.color} flexShrink={0} />
            <Typography fontSize="0.8rem" fontWeight={600} color="#475569">
              {item.label}:
            </Typography>
            <Typography fontSize="0.8rem" fontWeight={800} color="#1e293b">
              {formatNumber(item.value)}
            </Typography>
            <Typography fontSize="0.75rem" fontWeight={600} color="#94a3b8">
              ({pct}%)
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function ChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 2 }} />
    </Paper>
  );
}

function RankTableSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={180} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={2} py={1.5}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={18} sx={{ borderRadius: 1, ml: "auto" }} />
        </Box>
      ))}
    </Paper>
  );
}

export function SalesDashboardTab() {
  const [filters, setFilters] = useState<RelatorioFilterValues>({
    grupo: "todos",
    concessionaria: "todos",
    dataInicio: getFirstDayOfMonth(),
    dataFim: new Date().toISOString().split("T")[0] || "",
  });
  const [dashboard, setDashboard] = useState<DashboardGeral | null>(null);
  const [loading, setLoading] = useState(true);
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<KpiType | null>(null);

  const handleKpiClick = useCallback((type: KpiType) => {
    setSelectedKpi(type);
    setKpiDialogOpen(true);
  }, []);

  
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);
  const { user } = useUserStore();

  const resetFilters = useCallback(() => {
    setFilters({
      grupo: "todos",
      concessionaria: "todos",
      dataInicio: getFirstDayOfMonth(),
      dataFim: new Date().toISOString().split("T")[0] || "",
    });
    fetchDash();
  }, []);


  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchDashBoardSales({
        id_grupo_concessionaria: filters.grupo !== "todos" ? Number(filters.grupo) : null,
        id_concessionaria: filters.concessionaria !== "todos" ? Number(filters.concessionaria) : null,
        data_inicio: filters.dataInicio || null,
        data_fim: filters.dataFim || null,
      });
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashboard(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [filters, setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const volumeLabels = (dashboard?.volume_mensal ?? []).map(
    (v) => `${MONTH_NAMES[v.mes - 1]}/${v.ano}`,
  );

  const volumeChartData = {
    labels: volumeLabels,
    datasets: [
      {
        label: "Anúncios Vendidos",
        data: (dashboard?.volume_mensal ?? []).map((v) => v.qtd_anuncios_vendidos),
        borderColor: colorPrimary,
        backgroundColor: colorPrimary + "20",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: colorPrimary,
        yAxisID: "y",
      },
      {
        label: "Valor Total Vendido (R$)",
        data: (dashboard?.volume_mensal ?? []).map((v) => v.valor_total_vendido),
        borderColor: "#10b981",
        backgroundColor: "#10b98120",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: "#10b981",
        yAxisID: "y1",
      },
      {
        label: "Valorização (R$)",
        data: (dashboard?.volume_mensal ?? []).map((v) => v.valor_valorizacao),
        borderColor: "#8b5cf6",
        backgroundColor: "#8b5cf620",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: "#8b5cf6",
        borderDash: [6, 3],
        yAxisID: "y1",
      },
    ],
  };

  const volumeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            if (ctx.datasetIndex === 2) {
              const item = dashboard?.volume_mensal?.[ctx.dataIndex];
              const pct = item?.valor_valorizacao_percentual ?? 0;
              return ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)} (${pct.toFixed(2)}%)`;
            }
            if (ctx.datasetIndex >= 1) return ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`;
            return ` ${ctx.dataset.label}: ${ctx.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#94a3b8" },
      },
      y: {
        position: "left" as const,
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 }, color: "#94a3b8" },
        title: { display: true, text: "Qtd Anúncios", font: { size: 11, weight: 600 as const }, color: "#94a3b8" },
      },
      y1: {
        position: "right" as const,
        grid: { drawOnChartArea: false },
        ticks: {
          font: { size: 11 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val)),
        },
        title: { display: true, text: "Valor (R$)", font: { size: 11, weight: 600 as const }, color: "#94a3b8" },
      },
    },
  };

  const topMarcasData = {
    labels: (dashboard?.top_marcas ?? []).map((m) => m.marca.toUpperCase()),
    datasets: [
      {
        label: "Veículos",
        data: (dashboard?.top_marcas ?? []).map((m) => m.qtd_marca),
        backgroundColor: [
          colorPrimary, "#10b981", "#0284c7", "#8b5cf6", "#ef4444",
          "#f59e0b", "#ec4899", "#14b8a6", "#6366f1", "#78716c",
        ],
        borderRadius: 6,
        maxBarThickness: 40,
      },
    ],
  };

  const topMarcasOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 }, color: "#94a3b8" },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
    },
  };

  const acesso = dashboard?.volume_acesso;

  const totalConc = (dashboard?.top_concessionarias ?? []).reduce((sum, c) => sum + c.qtd_por_concessionaria, 0) || 1;

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <RelatorioFilterBar
            values={filters}
            onChange={setFilters}
            onSearch={fetchDash}
            onClear={resetFilters}
            loading={loading}
            title="Filtros do Dashboard"
          />
        </AnimatedBox>

        {/* KPI ROW 1 */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
              <KpiCardSkeleton delay={320} />
              <KpiCardSkeleton delay={400} />
              <KpiCardSkeleton delay={480} />
              <KpiCardSkeleton delay={560} />
              <KpiCardSkeleton delay={640} />
            </>
          ) : (
            <>
              <KpiCard
                label="Anúncios Publicados"
                value={formatNumber(dashboard?.qtd_publicados ?? 0)}
                subtitle="anúncios"
                icon={<Public />}
                accentColor="#6366f1"
                bgColor="#eef2ff"
                delay={400}
                onClick={() => handleKpiClick("publicados")}
              />

              <KpiCard
                label="Anúncios Vendidos"
                value={formatNumber(dashboard?.qtd_anuncios_vendidos ?? 0)}
                subtitle="anúncios"
                icon={<TrendingUp />}
                accentColor={colorPrimary}
                bgColor="#fff7ed"
                delay={160}
                onClick={() => handleKpiClick("vendidos")}
              />
              <KpiCard
                label="Anúncios Não Vendidos"
                value={formatNumber(dashboard?.qtd_anuncios_nao_vendidos ?? 0)}
                subtitle="anúncios"
                icon={<TrendingDown />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={240}
                onClick={() => handleKpiClick("nao_vendidos")}
              />
              <KpiCard
                label="Veículos Publicados"
                value={formatNumber(dashboard?.qtd_veiculos_publicados ?? 0)}
                subtitle="veículos"
                icon={<DirectionsCar />}
                accentColor="#8b5cf6"
                bgColor="#f5f3ff"
                delay={440}
                onClick={() => handleKpiClick("veiculos_publicados")}
              />
              <KpiCard
                label="Veículos Vendidos"
                value={formatNumber(dashboard?.qtd_veiculos_vendidos ?? 0)}
                subtitle="veículos"
                icon={<DirectionsCar />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={80}
                onClick={() => handleKpiClick("veiculos_vendidos")}
              />
              <KpiCard
                label="Veículos Não Vendidos"
                value={formatNumber(dashboard?.qtd_veiculos_nao_vendidos ?? 0)}
                subtitle="veículos"
                icon={<DirectionsCar />}
                accentColor="#f59e0b"
                bgColor="#fffbeb"
                delay={120}
                onClick={() => handleKpiClick("veiculos_nao_vendidos")}
              />
              <KpiCard
                label="Views Totais"
                value={formatNumber(dashboard?.qtd_views_totais ?? 0)}
                subtitle={`média ${formatNumber(dashboard?.qtd_media_views ?? 0)}/anúncio`}
                icon={<Visibility />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={240}
                onClick={() => handleKpiClick("views")}
              />
              <KpiCard
                label="Tempo Médio de Venda"
                value={formatMinutesToReadable(dashboard?.tempo_medio_venda ?? 0)}
                subtitle="até a venda"
                icon={<Timer />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={320}
                onClick={() => handleKpiClick("tempo_medio")}
              />
            </>
          )}
        </Box>

        {/* KPI ROW 2 */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "repeat(5, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={400} />
              <KpiCardSkeleton delay={460} />
              <KpiCardSkeleton delay={520} />
              <KpiCardSkeleton delay={580} />
              <KpiCardSkeleton delay={640} />
            </>
          ) : (
            <>
              <KpiCard
                label="Anúncios Compre Agora"
                value={formatNumber(dashboard?.qtd_anuncios_normais_vendidos ?? 0)}
                subtitle="vendidos"
                icon={<LocalOffer />}
                accentColor="#8b5cf6"
                bgColor="#f5f3ff"
                delay={400}
                onClick={() => handleKpiClick("compre_agora")}
              />
              <KpiCard
                label="Anúncios Leilão"
                value={formatNumber(dashboard?.qtd_anuncios_leilao_vendidos ?? 0)}
                subtitle="vendidos"
                icon={<Gavel />}
                accentColor="#f59e0b"
                bgColor="#fffbeb"
                delay={460}
                onClick={() => handleKpiClick("leilao")}
              />
              <KpiCard
                label="Anúncios Cotações"
                value={formatNumber(dashboard?.qtd_anuncios_cotacoes_vendidos ?? 0)}
                subtitle="vendidos"
                icon={<RequestQuote />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={520}
                onClick={() => handleKpiClick("cotacoes")}
              />
              <KpiCard
                label="Anúncios Combos"
                value={formatNumber(dashboard?.qtd_anuncios_combos_vendidos ?? 0)}
                subtitle="vendidos"
                icon={<Storefront />}
                accentColor="#14b8a6"
                bgColor="#f0fdfa"
                delay={580}
                onClick={() => handleKpiClick("combos")}
              />
              <KpiCard
                label="Média Propostas"
                value={(dashboard?.media_proposta_por_anuncio ?? 0).toFixed(1)}
                subtitle="por anúncio"
                icon={<TrendingUp />}
                accentColor="#6366f1"
                bgColor="#eef2ff"
                delay={640}
                onClick={() => handleKpiClick("media_propostas")}
              />
            </>
          )}
        </Box>

        {/* VOLUME MENSAL - Line Chart */}
        <AnimatedBox $delay={700}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Volume Mensal
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Evolução de anúncios vendidos, valor total vendido e valorização por mês
              </Typography>
              <Box height={320}>
                <Line data={volumeChartData} options={volumeChartOptions} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* KPI ROW - ACESSO */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={730} />
              <KpiCardSkeleton delay={790} />
              <KpiCardSkeleton delay={850} />
            </>
          ) : user?.grupoAcesso.id && user?.grupoAcesso.id.toString()=== UserRole.Admin ? (
            <>
              <KpiCard
                label="Usuários Cadastrados"
                value={formatNumber(acesso?.qtd_usuario ?? 0)}
                subtitle="usuários"
                icon={<People />}
                accentColor="#6366f1"
                bgColor="#eef2ff"
                delay={730}
              />
              <KpiCard
                label="Acessos no Período"
                value={formatNumber(acesso?.qtd_acesso_total ?? 0)}
                subtitle={`${formatNumber(acesso?.qtd_acesso_lojista ?? 0)} lojistas · ${formatNumber(acesso?.qtd_acesso_concessionaria ?? 0)} concessionárias`}
                icon={<Login />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={790}
              />
              <KpiCard
                label="Usuários Ativos"
                value={formatNumber(acesso?.qtd_usuario_logando ?? 0)}
                subtitle="logaram no período"
                icon={<HowToReg />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={850}
              />
            </>
          ) : null}
        </Box>

        {/* DOUGHNUTS - Acesso por Tipo e Plataforma */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
        {user?.grupoAcesso.id && user?.grupoAcesso.id.toString()=== UserRole.Admin ? (
          <AnimatedBox $delay={880}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Acessos por Tipo de Usuário
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição entre lojistas e concessionárias
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Lojistas", "Concessionárias"],
                      datasets: [{
                        data: [
                          acesso?.qtd_acesso_lojista ?? 0,
                          acesso?.qtd_acesso_concessionaria ?? 0,
                        ],
                        backgroundColor: [colorPrimary, "#0284c7"],
                        hoverBackgroundColor: [colorPrimary + "cc", "#0284c7cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Lojistas", value: acesso?.qtd_acesso_lojista ?? 0, color: colorPrimary },
                  { label: "Concessionárias", value: acesso?.qtd_acesso_concessionaria ?? 0, color: "#0284c7" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>
        ) : null}  
        {user?.grupoAcesso.id && user?.grupoAcesso.id.toString()=== UserRole.Admin ? (
                    <AnimatedBox $delay={910}>
                    {loading ? (
                      <ChartSkeleton />
                    ) : (
                      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                        <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                          Acessos por Plataforma
                        </Typography>
                        <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                          Distribuição de acessos entre Web, Android e iOS
                        </Typography>
                        <Box height={280} display="flex" alignItems="center" justifyContent="center">
                          <Doughnut
                            data={{
                              labels: ["Web", "Android", "iOS"],
                              datasets: [{
                                data: [
                                  acesso?.qtd_via_plataforma_web ?? 0,
                                  acesso?.qtd_via_plataforma_android ?? 0,
                                  acesso?.qtd_via_plataforma_ios ?? 0,
                                ],
                                backgroundColor: ["#6366f1", "#10b981", "#f59e0b"],
                                hoverBackgroundColor: ["#6366f1cc", "#10b981cc", "#f59e0bcc"],
                                borderWidth: 0,
                              }],
                            }}
                            options={doughnutOptions}
                          />
                        </Box>
                        <DoughnutLegend items={[
                          { label: "Web", value: acesso?.qtd_via_plataforma_web ?? 0, color: "#6366f1" },
                          { label: "Android", value: acesso?.qtd_via_plataforma_android ?? 0, color: "#10b981" },
                          { label: "iOS", value: acesso?.qtd_via_plataforma_ios ?? 0, color: "#f59e0b" },
                        ]} />
                      </Paper>
                    )}
                  </AnimatedBox>
        ) : null}

        </Box>

        {/* PIE CHARTS - Propostas e Compras por Plataforma */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={750}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Propostas por Plataforma
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de propostas entre Mobile e Web
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Mobile", "Web"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_proposta_mobile ?? 0,
                          dashboard?.qtd_proposta_web ?? 0,
                        ],
                        backgroundColor: [colorPrimary, "#0284c7"],
                        hoverBackgroundColor: [colorPrimary + "cc", "#0284c7cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Mobile", value: dashboard?.qtd_proposta_mobile ?? 0, color: colorPrimary },
                  { label: "Web", value: dashboard?.qtd_proposta_web ?? 0, color: "#0284c7" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={800}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Compras feitas via Compre agora por Plataforma
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de compras entre Mobile e Web
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Mobile", "Web"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_comprada_via_mobile ?? 0,
                          dashboard?.qtd_comprada_via_web ?? 0,
                        ],
                        backgroundColor: ["#10b981", "#8b5cf6"],
                        hoverBackgroundColor: ["#10b981cc", "#8b5cf6cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Mobile", value: dashboard?.qtd_comprada_via_mobile ?? 0, color: "#10b981" },
                  { label: "Web", value: dashboard?.qtd_comprada_via_web ?? 0, color: "#8b5cf6" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* PIE CHARTS - Leilão, Cotação e Combo por Plataforma */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={830}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Leilão Vendido por Plataforma
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de vendas em leilão entre Mobile e Web
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Mobile", "Web"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_vendido_leilao_mobile ?? 0,
                          dashboard?.qtd_vendido_leilao_web ?? 0,
                        ],
                        backgroundColor: ["#f59e0b", "#0284c7"],
                        hoverBackgroundColor: ["#f59e0bcc", "#0284c7cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Mobile", value: dashboard?.qtd_vendido_leilao_mobile ?? 0, color: "#f59e0b" },
                  { label: "Web", value: dashboard?.qtd_vendido_leilao_web ?? 0, color: "#0284c7" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={860}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Cotação Vendida por Plataforma
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de vendas em cotação entre Mobile e Web
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Mobile", "Web"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_vendido_cotacao_mobile ?? 0,
                          dashboard?.qtd_vendido_cotacao_web ?? 0,
                        ],
                        backgroundColor: ["#14b8a6", "#6366f1"],
                        hoverBackgroundColor: ["#14b8a6cc", "#6366f1cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Mobile", value: dashboard?.qtd_vendido_cotacao_mobile ?? 0, color: "#14b8a6" },
                  { label: "Web", value: dashboard?.qtd_vendido_cotacao_web ?? 0, color: "#6366f1" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={890}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Combo Vendido por Plataforma
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de vendas em combo entre Mobile e Web
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut
                    data={{
                      labels: ["Mobile", "Web"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_vendido_combo_mobile ?? 0,
                          dashboard?.qtd_vendido_combo_web ?? 0,
                        ],
                        backgroundColor: ["#e11d48", "#0ea5e9"],
                        hoverBackgroundColor: ["#e11d48cc", "#0ea5e9cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={doughnutOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Mobile", value: dashboard?.qtd_vendido_combo_mobile ?? 0, color: "#e11d48" },
                  { label: "Web", value: dashboard?.qtd_vendido_combo_web ?? 0, color: "#0ea5e9" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* TOP MARCAS + TOP MODELOS */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={900}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Top Marcas
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Marcas mais vendidas no período
                </Typography>
                <Box height={360}>
                  <Bar data={topMarcasData} options={topMarcasOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={1000}>
            {loading ? (
              <RankTableSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Top Modelos
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Modelos mais vendidos no período
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={thSx}>#</TableCell>
                        <TableCell sx={thSx}>Modelo</TableCell>
                        <TableCell sx={{ ...thSx, textAlign: "right" }}>Qtd</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboard?.top_modelos ?? []).map((m, i) => (
                        <TableRow key={m.modelo} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                            <RankBadge rank={i + 1} />
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1.5,
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: "#1e293b",
                              borderBottom: "1px solid #f8fafc",
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.modelo}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1.5,
                              fontSize: "0.85rem",
                              fontWeight: 800,
                              color: colorPrimary,
                              textAlign: "right",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {m.qtd_modelo}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* TOP CONCESSIONÁRIAS */}
        <AnimatedBox $delay={1100}>
          {loading ? (
            <RankTableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Top Concessionárias
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Concessionárias com mais vendas no período
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={thSx}>#</TableCell>
                      <TableCell sx={thSx}>Concessionária</TableCell>
                      <TableCell sx={thSx}>Vendas</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 200 }}>Proporção</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "right" }}>Valor Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dashboard?.top_concessionarias ?? []).map((c, i) => (
                      <TableRow key={c.concessionaria} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <RankBadge rank={i + 1} />
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {c.concessionaria}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: colorPrimary,
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {c.qtd_por_concessionaria}
                        </TableCell>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box flex={1}>
                              <LinearProgress
                                variant="determinate"
                                value={(c.qtd_por_concessionaria / totalConc) * 100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    bgcolor: colorPrimary,
                                  },
                                }}
                              />
                            </Box>
                            <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                              {Math.round((c.qtd_por_concessionaria / totalConc) * 100)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: "#10b981",
                            textAlign: "right",
                            borderBottom: "1px solid #f8fafc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatCurrency(c.valor_total_vendido)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>
        <KpiDetailDialog
          open={kpiDialogOpen}
          kpiType={selectedKpi}
          filters={filters}
          onClose={() => setKpiDialogOpen(false)}
        />
      </Box>
    </Fade>
  );
}

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

const thSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1.5,
  color: "#94a3b8",
  borderBottom: "2px solid #f1f5f9",
  py: 1.5,
  whiteSpace: "nowrap" as const,
};

const RANK_COLORS: Record<number, { bg: string; color: string }> = {
  1: { bg: "#fef3c7", color: "#d97706" },
  2: { bg: "#f1f5f9", color: "#64748b" },
  3: { bg: "#fff7ed", color: "#ea580c" },
};

function RankBadge({ rank }: { rank: number }) {
  const colors = RANK_COLORS[rank];
  if (colors) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={28}
        height={28}
        borderRadius="50%"
        bgcolor={colors.bg}
      >
        <EmojiEvents sx={{ fontSize: 16, color: colors.color }} />
      </Box>
    );
  }
  return (
    <Typography fontSize="0.8rem" fontWeight={700} color="#94a3b8" textAlign="center" width={28}>
      {rank}
    </Typography>
  );
}
